import { secureUuid } from '../uuid';
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { api, ApiError, type Game, type Room } from '../lobby/api';
import { useIssueContext } from './IssueContext';
import { captureViewport, imageFromFile } from './capture';
import { drawAnnotations, relativePoint, type Annotation, type Tool } from './annotations';
import './issues.css';

interface Draft {
  id: string; roomId: string | null; gameId: string | null; canvas: HTMLCanvasElement | null;
  context: Record<string, unknown>;
}
export function IssueReporter({ room, game, workspaceView }: { room: Room | null; game: Game | null; workspaceView?: string }) {
  const gameView = useIssueContext();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [notice, setNotice] = useState('');
  const lock = useRef(false);
  const capture = () => {
    if (lock.current || draft || document.querySelector('dialog[open]')) return;
    lock.current = true;
    const context = { capturedAt: new Date().toISOString(), captureMethod: 'app-rendered', clientBuild: __CLIENT_BUILD__,
      browser: navigator.userAgent, viewport: { width: innerWidth, height: innerHeight, devicePixelRatio, scrollX, scrollY },
      ui: { selectedTabs: [...document.querySelectorAll('[role="tab"][aria-selected="true"]')].map(el => el.textContent?.slice(0, 128)),
        openPanels: [...document.querySelectorAll('[role="dialog"], details[open]')].map(el => el.getAttribute('aria-label') ?? el.id),
        scrollContainers: [...document.querySelectorAll<HTMLElement>('main *, .scene-root *, .app-shell *')]
          .filter(el => el instanceof HTMLElement && (el.scrollTop !== 0 || el.scrollLeft !== 0)).slice(0, 100)
          .map(el => ({ id: el.id, classes: el.className, x: el.scrollLeft, y: el.scrollTop })) },
      page: window.location.pathname, workspaceView, game: game ? { id: game.id, name: game.name, catalogId: game.catalogId, variantId: game.variantId, status: game.status } : null,
      view: gameView?.current ? structuredClone(gameView.current) : null };
    const next: Draft = { id: reportId(), roomId: room?.id ?? null, gameId: game?.id ?? null, canvas: null, context };
    // Start the synchronous clone before any state update changes the page.
    const pending = captureViewport();
    setCapturing(true); setNotice('');
    void pending.then(canvas => { setDraft({ ...next, canvas }); }).catch(() => {
      setDraft(next); setNotice('Capture unavailable. Paste or choose a screenshot to continue.');
    }).finally(() => { lock.current = false; setCapturing(false); });
  };
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.altKey && e.shiftKey && !e.metaKey && !e.repeat && e.code === 'KeyR') { e.preventDefault(); capture(); }
    };
    window.addEventListener('keydown', key); return () => window.removeEventListener('keydown', key);
  });
  return <>
    <button type="button" className="club-text-btn issue-report-button" disabled={capturing} onClick={capture} title="Report visual issue (Ctrl+Alt+Shift+R)" aria-keyshortcuts="Control+Alt+Shift+R">{capturing ? 'Capturing…' : 'Report visual issue'}</button>
    {notice && !draft && <span className="issue-notice" role="status" data-issue-ui>{notice}</span>}
    {draft && createPortal(<IssueEditor draft={draft} initialError={notice} close={() => { setDraft(null); setNotice(''); }} saved={id => { setDraft(null); setNotice(`Issue saved · ${id.slice(0, 8)}`); }} />, document.body)}
  </>;
}

function reportId(): string { return secureUuid(); }

function IssueEditor({ draft, initialError, close, saved }: { draft: Draft; initialError: string; close: () => void; saved: (id: string) => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [original, setOriginal] = useState(draft.canvas);
  const [marks, setMarks] = useState<Annotation[]>([]);
  const [active, setActive] = useState<Annotation | null>(null);
  const current = useRef<Annotation | null>(null);
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState('#e11d48');
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState(initialError);
  const [replacement, setReplacement] = useState(false);
  // Retry exactly the same request after an uncertain response, preserving idempotency.
  const payload = useRef<object | null>(null);
  useEffect(() => {
    const el = dialog.current!; const previous = document.activeElement as HTMLElement | null;
    el.showModal(); return () => { el.close(); previous?.focus(); };
  }, []);
  useEffect(() => {
    const target = canvas.current;
    if (!target || !original) return;
    target.width = original.width; target.height = original.height;
    const ctx = target.getContext('2d')!; ctx.drawImage(original, 0, 0);
    drawAnnotations(ctx, active ? [...marks, active] : marks, target.width, target.height);
  }, [original, marks, active]);
  const replace = async (file: File) => {
    if (busyRef.current || payload.current) return;
    busyRef.current = true; setImageBusy(true);
    try { const image = await imageFromFile(file); setOriginal(image); setMarks([]); setActive(null); current.current = null; setReplacement(true); setError(''); }
    catch (err) { setError((err as Error).message); }
    finally { busyRef.current = false; setImageBusy(false); }
  };
  useEffect(() => {
    const paste = (e: ClipboardEvent) => {
      const file = [...(e.clipboardData?.items ?? [])].find(item => item.type.startsWith('image/'))?.getAsFile();
      if (file) { e.preventDefault(); void replace(file); }
    };
    document.addEventListener('paste', paste); return () => document.removeEventListener('paste', paste);
  });
  const point = (e: PointerEvent<HTMLCanvasElement>) => relativePoint(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect());
  const start = (e: PointerEvent<HTMLCanvasElement>) => {
    if (e.button !== 0 || current.current || busyRef.current || payload.current) return;
    if (marks.length >= 500) { setError('This report has reached 500 marks. Undo a mark to add another.'); return; }
    const p = point(e);
    if (tool === 'text') {
      if (!label.trim()) { setError('Type a text label, then click where it belongs.'); return; }
      setMarks(items => [...items, { tool, color, points: [p], text: label }]); setError(''); return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    current.current = { tool, color, points: [p] }; setActive(current.current);
  };
  const move = (e: PointerEvent<HTMLCanvasElement>) => {
    const mark = current.current; if (!mark) return;
    current.current = { ...mark, points: mark.tool === 'pen' ? mark.points.length < 2000 ? [...mark.points, point(e)] : mark.points : [mark.points[0]!, point(e)] };
    setActive(current.current);
  };
  const finish = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!current.current) return;
    move(e); const mark = current.current!; current.current = null; setActive(null); setMarks(items => [...items, mark]);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const save = async () => {
    if (!original || busyRef.current) return;
    busyRef.current = true; setBusy(true); setError('');
    try {
      if (!payload.current) {
        const out = document.createElement('canvas'); out.width = original.width; out.height = original.height;
        const ctx = out.getContext('2d')!; ctx.drawImage(original, 0, 0); drawAnnotations(ctx, marks, out.width, out.height);
        const source = original.toDataURL('image/png'), annotated = out.toDataURL('image/png');
        if (source.length > 8 * 1024 * 1024 || annotated.length > 8 * 1024 * 1024) throw new Error('The image is too large to save. Choose a smaller screenshot.');
        payload.current = { id: draft.id, gameId: draft.gameId, note, original: source, annotated, annotations: marks,
          context: { ...draft.context, captureMethod: replacement ? 'user-supplied' : 'app-rendered', image: { width: out.width, height: out.height } } };
      }
      const reply = await api<{ id: string }>(draft.roomId ? `/rooms/${encodeURIComponent(draft.roomId)}/issues` : '/issues', payload.current);
      saved(reply.id);
    } catch (err) {
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) payload.current = null;
      setError(`${(err as Error).message}. Your report is still here; retry saving.`);
    }
    finally { busyRef.current = false; setBusy(false); }
  };
  const locked = busy || imageBusy || payload.current !== null;
  return <dialog ref={dialog} className="issue-editor" aria-labelledby="issue-title" data-issue-ui onCancel={e => { if (busyRef.current) e.preventDefault(); else close(); }}>
    <header><div><h2 id="issue-title">Report visual issue</h2><p>Mark what looks wrong. Your current view is attached automatically.</p></div><button type="button" onClick={close} disabled={busy || imageBusy} aria-label="Cancel report">×</button></header>
    <div className="issue-tools" aria-label="Annotation tools">
      {(['pen', 'arrow', 'rectangle', 'text'] as const).map(t => <button type="button" key={t} aria-pressed={tool === t} disabled={locked} onClick={() => setTool(t)}>{t[0]!.toUpperCase() + t.slice(1)}</button>)}
      <label>Color <input type="color" aria-label="Annotation color" value={color} disabled={locked} onChange={e => setColor(e.target.value)} /></label>
      <button type="button" disabled={!marks.length || locked} onClick={() => setMarks(items => items.slice(0, -1))}>Undo</button>
      <label className="issue-upload">Choose screenshot<input type="file" accept="image/png,image/jpeg,image/webp" disabled={locked} onChange={e => { const f = e.target.files?.[0]; if (f) void replace(f); e.target.value = ''; }} /></label>
    </div>
    {tool === 'text' && <label className="issue-label">Text label <input value={label} maxLength={300} disabled={locked} onChange={e => setLabel(e.target.value)} placeholder="Type a label, then click the image" autoFocus /></label>}
    <div className="issue-image-area">{original ? <canvas ref={canvas} aria-label="Screenshot to annotate" onPointerDown={start} onPointerMove={move} onPointerUp={finish} onPointerCancel={() => { current.current = null; setActive(null); }} /> : <p>Paste a screenshot or choose an image above.</p>}</div>
    <footer><label>What looks wrong? <span>(optional)</span><textarea value={note} disabled={locked} onChange={e => setNote(e.target.value)} maxLength={8000} placeholder="For example: The card title is cut off." /></label>
      {error && <p role="alert">{error}</p>}
      <div className="issue-actions"><span>{replacement ? 'Using your screenshot' : 'Captured app view'} · {marks.length} marks</span><button type="button" onClick={close} disabled={busy || imageBusy}>Cancel</button><button type="button" className="issue-save" disabled={!original || busy || imageBusy || active !== null} onClick={() => void save()}>{imageBusy ? 'Loading image…' : busy ? 'Saving…' : payload.current ? 'Retry save' : 'Save issue'}</button></div>
    </footer>
  </dialog>;
}
